import React, { useEffect, useState } from "react";
import adminApi from "../../services/adminApi";
import toast from "react-hot-toast";
import LoadingState from "../../components/Admin/LoadingState";
import "../../styles/Admin.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function MenuManager() {
  const [menuItems, setMenuItems] = useState(null);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ text: "", link: "", order: 0 });
  const [editingId, setEditingId] = useState(null);

  const loadMenuItems = () => {
    adminApi
      .get("/admin/menu/items")
      .then((res) => setMenuItems(res.data))
      .catch(() => toast.error("Failed to load menu items"));
  };

  const loadLogo = () => {
    adminApi
      .get("/admin/menu/logo")
      .then((res) => setLogo(res.data.logo))
      .catch(() => toast.error("Failed to load logo"));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminApi.get("/admin/menu/items").then((res) => setMenuItems(res.data)),
      adminApi.get("/admin/menu/logo").then((res) => setLogo(res.data.logo)),
    ])
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveMenuItem = async () => {
    if (!form.text.trim() || !form.link.trim()) {
      toast.error("Text and link are required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/menu/items/${editingId}`, form);
        toast.success("Menu item updated successfully");
      } else {
        await adminApi.post("/admin/menu/items", form);
        toast.success("Menu item created successfully");
      }
      setForm({ text: "", link: "", order: 0 });
      setEditingId(null);
      loadMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save menu item");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({ text: item.text, link: item.link, order: item.order });
    setEditingId(item.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this menu item?")) {
      return;
    }
    try {
      await adminApi.delete(`/admin/menu/items/${id}`);
      toast.success("Menu item deleted successfully");
      loadMenuItems();
    } catch (error) {
      toast.error("Failed to delete menu item");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (100KB = 100 * 1024 bytes)
    if (file.size > 100 * 1024) {
      toast.error("File size must be less than 100KB");
      e.target.value = "";
      return;
    }

    // Check if it's an image
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      e.target.value = "";
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("logo", file);

    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_URL}/admin/menu/logo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to upload logo");
      }

      const data = await response.json();
      setLogo(data.logo);
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm("Are you sure you want to delete the logo?")) {
      return;
    }
    try {
      await adminApi.delete("/admin/menu/logo");
      setLogo(null);
      toast.success("Logo deleted successfully");
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const getLogoUrl = () => {
    if (!logo || !logo.path) return null;
    // Remove /api from the end if present, then append the logo path
    const baseUrl = API_URL.endsWith("/api") 
      ? API_URL.slice(0, -4) 
      : API_URL.replace("/api", "");
    return `${baseUrl}${logo.path}`;
  };

  if (loading) {
    return <LoadingState message="Loading menu manager..." />;
  }

  return (
    <>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Menu Manager</h1>
        <p className="admin-page-subtitle">
          Manage menu links, text, and site logo
        </p>
      </div>

      {/* Site Logo Section */}
      <div className="admin-section" style={{ marginBottom: 32 }}>
        <div className="admin-section-header">
          <h3 className="admin-section-title">Site Logo</h3>
          <p className="admin-section-subtitle">
            Upload or delete the site logo (max 100KB)
          </p>
        </div>

        <div className="admin-form">
          {logo && getLogoUrl() ? (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: 16,
                  background: "#f8f9fa",
                  borderRadius: 6,
                  border: "1px solid #e9ecef",
                }}
              >
                <img
                  src={getLogoUrl()}
                  alt="Site Logo"
                  style={{
                    maxHeight: 60,
                    maxWidth: 200,
                    objectFit: "contain",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                    Current Logo
                  </div>
                  <div style={{ fontSize: 12, color: "#6c757d" }}>
                    {logo.originalName} ({(logo.size / 1024).toFixed(2)} KB)
                  </div>
                </div>
                <button
                  className="btn-secondary"
                  onClick={handleDeleteLogo}
                  style={{
                    background: "#fee2e2",
                    color: "#dc2626",
                    borderColor: "#fecaca",
                  }}
                >
                  Delete Logo
                </button>
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                background: "#f8f9fa",
                borderRadius: 6,
                border: "1px dashed #dee2e6",
                color: "#6c757d",
                marginBottom: 16,
              }}
            >
              No logo uploaded
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Upload Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploading}
              className="form-control"
            />
            <small className="form-text text-muted">
              Maximum file size: 100KB. Allowed formats: JPEG, PNG, GIF, WebP
            </small>
          </div>
        </div>
      </div>

      {/* Menu Items Section */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h3 className="admin-section-title">Menu Items</h3>
          <p className="admin-section-subtitle">
            Add, edit, or delete menu links and text
          </p>
        </div>

        {/* Add/Edit Form */}
        <div className="admin-form" style={{ marginBottom: 24 }}>
          <div className="form-group">
            <label className="form-label">Menu Text</label>
            <input
              type="text"
              className="form-control"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="e.g., Dashboard"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Menu Link</label>
            <input
              type="text"
              className="form-control"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="e.g., /dashboard"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Order</label>
            <input
              type="number"
              className="form-control"
              value={form.order}
              onChange={(e) =>
                setForm({ ...form, order: parseInt(e.target.value) || 0 })
              }
              placeholder="0"
            />
            <small className="form-text text-muted">
              Lower numbers appear first in the menu
            </small>
          </div>

          <div className="admin-form-actions">
            <button
              className="btn-primary"
              onClick={handleSaveMenuItem}
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : editingId
                ? "Update Menu Item"
                : "Add Menu Item"}
            </button>
            {editingId && (
              <button
                className="btn-secondary"
                onClick={() => {
                  setForm({ text: "", link: "", order: 0 });
                  setEditingId(null);
                }}
                style={{ marginLeft: 8 }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Menu Items Table */}
        {menuItems && menuItems.length > 0 ? (
          <div
            style={{
              border: "1px solid #e9ecef",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "#fff",
              }}
            >
              <thead>
                <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#495057",
                    }}
                  >
                    Text
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#495057",
                    }}
                  >
                    Link
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#495057",
                    }}
                  >
                    Order
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "right",
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#495057",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {menuItems.map((item) => (
                  <tr
                    key={item.id}
                    style={{
                      borderBottom: "1px solid #e9ecef",
                    }}
                  >
                    <td style={{ padding: "12px 16px", fontSize: 14 }}>
                      {item.text}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 14 }}>
                      <code
                        style={{
                          background: "#f8f9fa",
                          padding: "2px 6px",
                          borderRadius: 3,
                          fontSize: 12,
                        }}
                      >
                        {item.link}
                      </code>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 14 }}>
                      {item.order}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                      }}
                    >
                      <button
                        className="btn-secondary"
                        onClick={() => handleEdit(item)}
                        style={{
                          padding: "6px 12px",
                          fontSize: 13,
                          marginRight: 8,
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => handleDelete(item.id)}
                        style={{
                          padding: "6px 12px",
                          fontSize: 13,
                          background: "#fee2e2",
                          color: "#dc2626",
                          borderColor: "#fecaca",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              background: "#f8f9fa",
              borderRadius: 6,
              border: "1px dashed #dee2e6",
              color: "#6c757d",
            }}
          >
            No menu items. Add one using the form above.
          </div>
        )}
      </div>
    </>
  );
}

